// Fill-amount maths shared by the dispatch getters.
//
// `fillAmount` is what a site needs TODAY to reach the maximum fill level:
//     fillAmount = capacity × maxFillPct/100 − volume
//
// `predictedFillAmount` is what it will need ON THE DISPATCH DATE. The site's
// Injection Avg ages the volume forward to that date:
//     predictedVolume     = max(0, volume − injectionAvg × daysUntilDispatch)
//     predictedFillAmount = capacity × maxFillPct/100 − predictedVolume
//
// ASSUMPTION: Injection Avg is litres per day. That's the same basis as DTE
// (days to empty) in this dataset — DTE = volume / Injection Avg — and the
// property is logged once daily. If the backend ever changes that basis, this
// is the only place that needs to know.
//
// Flooring predictedVolume at 0 keeps the answer physical: a tank that runs dry
// before the dispatch date needs a full load to the max fill line, never more.
// A site with no usable Injection Avg predicts as its current fillAmount rather
// than dropping out, so totals stay summable.

const MS_PER_DAY = 86400000;

/**
 * Whole days from today to the dispatch date, both taken at local midnight so a
 * same-day dispatch is 0 rather than a fraction. A missing or past date gives 0
 * — the forecast never runs backwards.
 */
export function daysUntil(dispatchDate, now = new Date()) {
  if (dispatchDate == null || dispatchDate === '') return 0;
  const target = new Date(dispatchDate);
  if (isNaN(target.getTime())) return 0;
  const targetDay = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((targetDay - today) / MS_PER_DAY));
}

/**
 * Current and predicted litres-to-deliver for one site.
 *
 * @param {object} input
 * @param {*} input.capacity      tank capacity (litres)
 * @param {*} input.volume        current volume (litres)
 * @param {*} input.injectionAvg  average daily draw (litres/day); missing = no growth
 * @param {number} input.maxFillPct  fill ceiling as a percentage
 * @param {number} input.days     days until the dispatch date
 * @returns {{fillAmount: number|null, predictedFillAmount: number|null, injectionAvg: number|null}}
 *   Both amounts are null when capacity or volume is unusable — the caller decides
 *   how to present "unknown".
 */
export function computeFillAmounts({ capacity, volume, injectionAvg, maxFillPct, days }) {
  const rate = parseFloat(injectionAvg);
  const usableRate = isNaN(rate) || rate <= 0 ? 0 : rate;

  const cap = parseFloat(capacity);
  const vol = parseFloat(volume);
  if (isNaN(cap) || isNaN(vol)) {
    return {
      fillAmount: null,
      predictedFillAmount: null,
      injectionAvg: isNaN(rate) ? null : rate,
    };
  }

  const maxFillVolume = cap * (Number(maxFillPct) / 100);
  const forecastDays = Math.max(0, Number(days) || 0);
  const predictedVolume = Math.max(0, vol - usableRate * forecastDays);

  return {
    fillAmount: Math.round(maxFillVolume - vol),
    predictedFillAmount: Math.round(maxFillVolume - predictedVolume),
    injectionAvg: isNaN(rate) ? null : rate,
  };
}
